<template>
  <v-form ref="formRef" @submit.prevent="onSubmit">
    <v-container>
      <v-row>
        <v-col cols="12">
          <v-text-field
            :disabled="!canEdit || isLoadingRole"
            :loading="isLoadingRole"
            :rules="[validationRules.required]"
            v-model="form.description"
            label="Descrição"
            name="description"
            type="text"
          />
        </v-col>
        <v-col cols="12" v-if="!(roleId && role?.is_master)">
          <permission-matrix
            v-model="form.permission_ids"
            :role-type="context"
            :disabled="!canEdit"
          />
        </v-col>
        <v-col cols="12" class="text-right">
          <v-btn
            :size="isMobile ? 'small' : 'default'"
            @click="onDelete"
            color="error"
            class="mr-2"
            :disabled="isSubmitting"
            v-if="
              roleId &&
              $can('role:delete') &&
              !role?.is_master &&
              !isLoadingRole
            "
          >
            Excluir
          </v-btn>
          <v-btn
            v-if="canEdit && !isLoadingRole"
            type="submit"
            color="primary"
            :loading="isSubmitting"
            :size="isMobile ? 'small' : 'default'"
          >
            Salvar
          </v-btn>
        </v-col>
        <v-dialog v-model="confirmDeleteDialog" width="600">
          <v-card flat>
            <v-container>
              <v-toolbar flat color="transparent">
                <v-toolbar-title>Confirmação de exclusão</v-toolbar-title>
              </v-toolbar>
              <v-card-text>
                Tem certeza que deseja excluir este perfil?
              </v-card-text>
              <v-card-actions>
                <v-row>
                  <v-col cols="12" class="text-right">
                    <v-btn
                      color="error"
                      class="mr-4"
                      @click="onConfirmDeleteRole"
                      :loading="isDeleting"
                    >
                      Excluir
                    </v-btn>
                    <v-btn @click="confirmDeleteDialog = false">
                      Cancelar
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-container>
          </v-card>
        </v-dialog>
      </v-row>
    </v-container>
  </v-form>
</template>

<script setup>
import { ref, computed, watch, inject } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { validationRules } from "@/utils/validation";
import PermissionMatrix from "@/components/PermissionMatrix";
import {
  useRoleQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} from "@/queries/modules/roles";
import { useSnackbar } from "@/plugins/snackbar";
import { useMetaStore } from "@/stores/meta";

const route = useRoute();
const router = useRouter();
const snackbar = useSnackbar();
const $can = inject("$can");
const { mobile } = useDisplay();
const metaStore = useMetaStore();

// Refs
const formRef = ref(null);
const confirmDeleteDialog = ref(false);
const form = ref({
  description: "",
  permission_ids: [],
});
const context = ref(route.meta.context);
const roleId = computed(() =>
  route.params.role_id ? Number(route.params.role_id) : null,
);

// Set page title
metaStore.setTitle(roleId.value ? "Editar Perfil" : "Novo Perfil");

// TanStack Query - fetch role data
const {
  data: role,
  isLoading: isLoadingRole,
  error: roleError,
} = useRoleQuery(roleId);

// TanStack Query - mutations
const createRoleMutation = useCreateRoleMutation();
const updateRoleMutation = useUpdateRoleMutation();
const deleteRoleMutation = useDeleteRoleMutation();

// Computed
const isMobile = computed(() => mobile.value);

const isInEditMode = computed(() => !!roleId.value);

const canEdit = computed(() => {
  if (isInEditMode.value) {
    return $can("role:update") && !role.value?.is_master;
  }
  return $can("role:create");
});

const isSubmitting = computed(
  () =>
    createRoleMutation.isPending.value || updateRoleMutation.isPending.value,
);

const isDeleting = computed(() => deleteRoleMutation.isPending.value);

// Watch role data to populate form
watch(
  role,
  (roleData) => {
    if (roleData) {
      form.value.description = roleData.description;
      form.value.permission_ids = roleData.permissions?.map((p) => p.id) || [];
    }
  },
  { immediate: true },
);

// Watch for errors
watch(roleError, (err) => {
  if (err) {
    snackbar.error(err.message);
  }
});

// Methods
const onSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    return;
  }

  try {
    const payload = {
      description: form.value.description,
      permission_ids: form.value.permission_ids,
      type: context.value,
    };

    if (!isInEditMode.value) {
      await createRoleMutation.mutateAsync(payload);
    } else {
      await updateRoleMutation.mutateAsync({
        roleId: roleId.value,
        data: payload,
      });
    }
    router.push({ name: "roles.index" });
  } catch (e) {
    snackbar.error(e.message);
  }
};

const onDelete = () => {
  confirmDeleteDialog.value = true;
};

const onConfirmDeleteRole = async () => {
  try {
    await deleteRoleMutation.mutateAsync(roleId.value);
    router.push({ name: "roles.index" });
  } catch (e) {
    snackbar.error(e.message);
  } finally {
    confirmDeleteDialog.value = false;
  }
};
</script>

<style scoped></style>
